import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  MessageSquare, Camera, Mic, BookOpen, ClipboardCheck, 
  Brain, AlertTriangle, GraduationCap, Target, FileText,
  Users, TrendingUp, Calendar
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUserCompanyId } from '@/hooks/useSubscriptionFeatures';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface FeatureUsage {
  feature_type: string;
  usage_count: number;
  last_used: string;
}

interface DailyUsage {
  usage_date: string;
  usage_count: number;
}

interface TopUser {
  worker_id: string;
  display_name: string;
  usage_count: number;
  top_feature: string;
}

// Feature type display info
const FEATURE_INFO: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  chat: { label: 'Chat SORIN', icon: <MessageSquare className="h-4 w-4" />, color: 'hsl(var(--primary))' },
  photo_analysis: { label: 'Analisis Foto', icon: <Camera className="h-4 w-4" />, color: 'hsl(var(--success))' },
  voice_transcription: { label: 'Transkripsi Suara', icon: <Mic className="h-4 w-4" />, color: 'hsl(var(--warning))' },
  knowledge_query: { label: 'Knowledge Base', icon: <BookOpen className="h-4 w-4" />, color: 'hsl(210, 80%, 50%)' },
  inspection: { label: 'Inspeksi AI', icon: <ClipboardCheck className="h-4 w-4" />, color: 'hsl(280, 70%, 50%)' },
  copilot: { label: 'Safety Copilot', icon: <Brain className="h-4 w-4" />, color: 'hsl(340, 70%, 50%)' },
  incident_extraction: { label: 'Ekstraksi Insiden', icon: <AlertTriangle className="h-4 w-4" />, color: 'hsl(0, 70%, 50%)' },
  incident_investigation: { label: 'Investigasi Insiden', icon: <AlertTriangle className="h-4 w-4" />, color: 'hsl(15, 70%, 50%)' },
  learning_content: { label: 'Konten Pembelajaran', icon: <GraduationCap className="h-4 w-4" />, color: 'hsl(120, 60%, 40%)' },
  intervention_content: { label: 'Konten Intervensi', icon: <Target className="h-4 w-4" />, color: 'hsl(45, 80%, 45%)' },
  intervention_recommendations: { label: 'Rekomendasi Intervensi', icon: <Target className="h-4 w-4" />, color: 'hsl(60, 70%, 45%)' },
  document_processing: { label: 'Pemrosesan Dokumen', icon: <FileText className="h-4 w-4" />, color: 'hsl(200, 60%, 45%)' },
  intent_detection: { label: 'Deteksi Intent', icon: <Brain className="h-4 w-4" />, color: 'hsl(260, 50%, 50%)' },
  alert_actions: { label: 'Aksi Alert', icon: <AlertTriangle className="h-4 w-4" />, color: 'hsl(30, 80%, 50%)' },
  checkin_processing: { label: 'Pemrosesan Check-in', icon: <Users className="h-4 w-4" />, color: 'hsl(180, 60%, 45%)' },
  image_generation: { label: 'Generasi Gambar', icon: <Camera className="h-4 w-4" />, color: 'hsl(300, 60%, 50%)' },
};

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--success))',
  'hsl(var(--warning))',
  'hsl(210, 80%, 50%)',
  'hsl(280, 70%, 50%)',
  'hsl(340, 70%, 50%)',
  'hsl(0, 70%, 50%)',
  'hsl(120, 60%, 40%)',
  'hsl(45, 80%, 45%)',
  'hsl(200, 60%, 45%)',
];

const AIUsageBreakdown = () => {
  const [featureUsage, setFeatureUsage] = useState<FeatureUsage[]>([]);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const { companyId, isLoading: isCompanyLoading } = useUserCompanyId();

  useEffect(() => {
    if (companyId && !isCompanyLoading) {
      fetchUsageData();
    }
  }, [companyId, isCompanyLoading, timeRange]);

  const fetchUsageData = async () => {
    if (!companyId) return;
    setIsLoading(true);

    try {
      // Fetch all data in parallel
      const [featureResult, dailyResult, usersResult] = await Promise.all([
        supabase.rpc('get_ai_usage_summary', { 
          p_company_id: companyId, 
          p_days: parseInt(timeRange) 
        }),
        supabase.rpc('get_ai_usage_daily', { 
          p_company_id: companyId, 
          p_days: parseInt(timeRange) 
        }),
        supabase.rpc('get_ai_top_users', { 
          p_company_id: companyId, 
          p_days: parseInt(timeRange),
          p_limit: 10
        }),
      ]);

      if (featureResult.data) {
        setFeatureUsage(featureResult.data as FeatureUsage[]);
      }
      if (dailyResult.data) {
        setDailyUsage((dailyResult.data as DailyUsage[]).reverse());
      }
      if (usersResult.data) {
        setTopUsers(usersResult.data as TopUser[]);
      }
    } catch (error) {
      console.error('Error fetching AI usage data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalUsage = featureUsage.reduce((sum, f) => sum + f.usage_count, 0);

  // Prepare pie chart data
  const pieData = featureUsage.map((f, idx) => ({
    name: FEATURE_INFO[f.feature_type]?.label || f.feature_type,
    value: f.usage_count,
    fill: CHART_COLORS[idx % CHART_COLORS.length],
  }));

  // Prepare bar chart data for daily usage
  const barData = dailyUsage.map(d => ({
    date: new Date(d.usage_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
    usage: d.usage_count,
  }));

  if (isLoading || isCompanyLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with time range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Riwayat Penggunaan AI
          </h3>
          <p className="text-sm text-muted-foreground">
            Total: {totalUsage.toLocaleString()} percakapan dalam {timeRange} hari terakhir
          </p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[140px]">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Hari</SelectItem>
            <SelectItem value="14">14 Hari</SelectItem>
            <SelectItem value="30">30 Hari</SelectItem>
            <SelectItem value="90">90 Hari</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {totalUsage === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Belum ada penggunaan AI</p>
            <p className="text-sm mt-1">Data akan muncul setelah fitur AI digunakan</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="breakdown" className="space-y-4">
          <TabsList>
            <TabsTrigger value="breakdown">Per Fitur</TabsTrigger>
            <TabsTrigger value="daily">Harian</TabsTrigger>
            <TabsTrigger value="users">Top Pengguna</TabsTrigger>
          </TabsList>

          {/* Feature Breakdown Tab */}
          <TabsContent value="breakdown" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Distribusi per Fitur</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: number) => [`${value} (${((value / totalUsage) * 100).toFixed(1)}%)`, 'Penggunaan']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Feature List */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detail per Fitur</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {featureUsage.map((f, idx) => {
                      const info = FEATURE_INFO[f.feature_type] || { 
                        label: f.feature_type, 
                        icon: <Brain className="h-4 w-4" />,
                        color: CHART_COLORS[idx % CHART_COLORS.length]
                      };
                      const percentage = ((f.usage_count / totalUsage) * 100).toFixed(1);
                      
                      return (
                        <div key={f.feature_type} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div 
                              className="p-2 rounded-lg" 
                              style={{ backgroundColor: `${info.color}20` }}
                            >
                              <span style={{ color: info.color }}>{info.icon}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{info.label}</p>
                              <p className="text-xs text-muted-foreground">
                                Terakhir: {new Date(f.last_used).toLocaleDateString('id-ID')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{f.usage_count.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">{percentage}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Daily Usage Tab */}
          <TabsContent value="daily">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tren Penggunaan Harian</CardTitle>
                <CardDescription>
                  Jumlah percakapan AI per hari
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      interval={Math.ceil(barData.length / 10)}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value: number) => [value, 'Percakapan']}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar 
                      dataKey="usage" 
                      fill="hsl(var(--primary))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Top Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Top 10 Pengguna AI
                </CardTitle>
                <CardDescription>
                  Pekerja dengan penggunaan AI tertinggi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {topUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>Belum ada data pengguna</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {topUsers.map((user, idx) => {
                      const featureInfo = FEATURE_INFO[user.top_feature] || { 
                        label: user.top_feature, 
                        icon: <Brain className="h-3 w-3" /> 
                      };
                      
                      return (
                        <div key={user.worker_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              idx === 0 ? 'bg-warning/20 text-warning' :
                              idx === 1 ? 'bg-muted text-muted-foreground' :
                              idx === 2 ? 'bg-warning/10 text-warning/80' :
                              'bg-muted/50 text-muted-foreground'
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{user.display_name || 'Unknown'}</p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                {featureInfo.icon}
                                <span>Paling sering: {featureInfo.label}</span>
                              </div>
                            </div>
                          </div>
                          <Badge variant="secondary" className="font-mono">
                            {user.usage_count.toLocaleString()} 
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AIUsageBreakdown;
